import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProjects } from './ProjectsContext';
import { useAuth } from './AuthContext';
import * as XLSX from 'xlsx';

export default function NewProject() {
  const navigate = useNavigate();
  const location = useLocation();
  const { saveProject, updateProject } = useProjects();
  const { currentUser, logout } = useAuth();
  const editProject = location.state?.project;
  const isEditMode = !!editProject;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [form, setForm] = useState({
    location: { country: '', city: '' },
    stpType: '',
    description: '',
    driver: '',
    recommendation: '',
    preSpend: '',
    nextSteps: '',
    modelDate: { from: '', to: '' },
    decision: '',
    goa: { leaseLiability: '', capexLiability: '' },
    pnlImpact: '',
    rag: {},
    riskItems: {},
    financialSummary: null
  });
  const [excelFile, setExcelFile] = useState(null);

  useEffect(() => {
    if (editProject) {
      setForm(editProject);
    }
  }, [editProject]);
  const colorMap = {
    green: '#28a745',
    amber: '#ffc107',
    red: '#dc3545',
    grey: '#6c757d'
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setForm({ ...form, [parent]: { ...form[parent], [child]: value } });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleRagClick = (item) => {
    const colors = ['green', 'amber', 'red', 'grey'];
    const current = form.rag[item] || 'green';
    const currentIndex = colors.indexOf(current);
    const nextIndex = (currentIndex + 1) % colors.length;
    const nextColor = colors[nextIndex];
    setForm({ ...form, rag: { ...form.rag, [item]: nextColor } });
  };

  const handleRiskChange = (item, note) => {
    setForm({ ...form, riskItems: { ...form.riskItems, [item]: note } });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExcelFile(file);

    // Read the Excel file
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'array', cellStyles: true });

        // Get the 'Business Case Financial' sheet
        const sheetName = 'Business Case Financial';
        const worksheet = workbook.Sheets[sheetName];

        if (!worksheet) {
          alert(`Sheet "${sheetName}" not found in the Excel file.`);
          return;
        }

        // Extract range C3:M27 while respecting hidden rows/columns
        const range = XLSX.utils.decode_range('C3:M27');
        const tableData = [];

        // Get hidden rows and columns information
        const hiddenRows = new Set();
        const hiddenCols = new Set();

        // Check for hidden rows (row height = 0)
        if (worksheet['!rows']) {
          worksheet['!rows'].forEach((rowInfo, idx) => {
            if (rowInfo && rowInfo.hidden) {
              hiddenRows.add(idx);
            }
          });
        }

        // Check for hidden columns (column width = 0)
        if (worksheet['!cols']) {
          worksheet['!cols'].forEach((colInfo, idx) => {
            if (colInfo && colInfo.hidden) {
              hiddenCols.add(idx);
            }
          });
        }

        // Extract data from C3:M27
        for (let R = range.s.r; R <= range.e.r; ++R) {
          // Skip hidden rows
          if (hiddenRows.has(R)) continue;

          const row = [];
          const excelRowNum = R + 1; // Store Excel row number (1-based)

          for (let C = range.s.c; C <= range.e.c; ++C) {
            // Skip hidden columns
            if (hiddenCols.has(C)) continue;

            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = worksheet[cellAddress];

            // Get cell value and formatting
            let value = '';
            if (cell) {
              if (cell.v !== undefined) {
                value = cell.v;
              }
              // Format numbers if cell has number format
              if (cell.t === 'n' && cell.z) {
                value = XLSX.SSF.format(cell.z, cell.v);
              }
            }

            row.push({
              value: value,
              excelRowNum: excelRowNum, // Store row number in each cell
              style: cell ? {
                bold: cell.s && cell.s.font && cell.s.font.bold,
                italic: cell.s && cell.s.font && cell.s.font.italic,
                alignment: cell.s && cell.s.alignment
              } : {}
            });
          }

          if (row.length > 0) {
            tableData.push(row);
          }
        }

        setForm({ ...form, financialSummary: tableData });
      } catch (error) {
        console.error('Error reading Excel file:', error);
        alert('Error reading Excel file. Please make sure it is a valid Excel file.');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSave = async () => {
    try {
      if (isEditMode) {
        await updateProject(editProject.id, form);
      } else {
        await saveProject(form);
      }
      navigate('/existing-projects');
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const ragItems = [
    'Green lease compliant', '3-day working week capacity', 'Rent within ABP',
    'Capital project within ABP', 'Mark-to-market', 'LOC satisfaction',
    'Occupancy data availability', 'Design standards', 'Security deposit',
    'EHS assessment', 'Seismic assessment', 'ABAC compliant',
    'Reinstatement provision', 'Registered address'
  ];

  const ragLabel = form.stpType === 'STP0' ? '(current)' : '(proposed)';

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f9f9f9',
      borderRadius: '8px',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Return to Home
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: '#666', fontSize: '14px' }}>{currentUser?.username}</span>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Logout
          </button>
        </div>
      </div>
      <h1 style={{ textAlign: 'center', color: '#333' }}>{isEditMode ? 'Edit Project' : 'New Project'}</h1>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <h2 style={{ color: '#555' }}>Location</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input name="location.country" placeholder="Country" value={form.location.country} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', flex: 1 }} />
          <input name="location.city" placeholder="City" value={form.location.city} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', flex: 1 }} />
        </div>

        <h2 style={{ color: '#555' }}>STP Type</h2>
        <select name="stpType" value={form.stpType} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
          <option value="">Select</option>
          <option value="STP0">STP0</option>
          <option value="STP1">STP1</option>
          <option value="STP2">STP2</option>
        </select>

        <h2 style={{ color: '#555' }}>Project Description</h2>
        <textarea name="description" placeholder="e.g. pre-spend request" value={form.description} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '60px' }} />

        {form.stpType === 'STP0' && (
          <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '4px', backgroundColor: '#fff' }}>
            <h3 style={{ color: '#555' }}>Driver</h3>
            <input name="driver" placeholder="Insert placeholder" value={form.driver} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%' }} />
            <h3 style={{ color: '#555' }}>Recommendation</h3>
            <input name="recommendation" placeholder="Insert placeholder" value={form.recommendation} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%' }} />
            <h3 style={{ color: '#555' }}>Pre-spend Request</h3>
            <input name="preSpend" placeholder="Insert placeholder" value={form.preSpend} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%' }} />
            <h3 style={{ color: '#555' }}>Next Steps</h3>
            <textarea name="nextSteps" placeholder="Insert placeholder" value={form.nextSteps} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '60px', width: '100%' }} />
            <h3 style={{ color: '#555' }}>Model Date</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input name="modelDate.from" type="date" value={form.modelDate.from} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
              <input name="modelDate.to" type="date" value={form.modelDate.to} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
            </div>
          </div>
        )}

        {(form.stpType === 'STP1' || form.stpType === 'STP2') && (
          <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '4px', backgroundColor: '#fff' }}>
            <h3 style={{ color: '#555' }}>Driver</h3>
            <input name="driver" placeholder="Insert placeholder" value={form.driver} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%' }} />
            <h3 style={{ color: '#555' }}>Decision</h3>
            <input name="decision" placeholder="Insert placeholder" value={form.decision} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%' }} />
            <h3 style={{ color: '#555' }}>GOA</h3>
            <input name="goa.leaseLiability" placeholder="Lease liability" value={form.goa.leaseLiability} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', marginBottom: '8px' }} />
            <input name="goa.capexLiability" placeholder="Capex liability" value={form.goa.capexLiability} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', marginBottom: '8px' }} />
            <div style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              Total £: {(() => {
                const lease = parseFloat(form.goa.leaseLiability) || 0;
                const capex = parseFloat(form.goa.capexLiability) || 0;
                const total = lease + capex;
                return total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              })()}
            </div>
            <h3 style={{ color: '#555', marginTop: '15px' }}>P&L Impact</h3>
            <input name="pnlImpact" placeholder="Total P&L summary" value={form.pnlImpact} onChange={handleChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%' }} />
          </div>
        )}

        <h2>RAG Assessment {ragLabel}</h2>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>Item</th>
              <th style={{ border: '1px solid #ddd', padding: '8px' }}>RAG</th>
            </tr>
          </thead>
          <tbody>
            {ragItems.map(item => (
              <tr key={item}>
                <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                  <button
                    type="button"
                    style={{
                      backgroundColor: colorMap[form.rag[item]] || 'white',
                      border: '1px solid #000',
                      padding: '10px',
                      cursor: 'pointer',
                      width: '50px',
                      height: '30px'
                    }}
                    onClick={() => handleRagClick(item)}
                  >
                    
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 style={{ color: '#555' }}>Project Risk Items</h2>
        {Object.entries(form.rag).filter(([_, color]) => color === 'amber' || color === 'red').map(([item]) => (
          <div key={item} style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '4px', backgroundColor: '#fff', marginBottom: '10px' }}>
            <h4 style={{ color: '#555' }}>{item}</h4>
            <textarea placeholder="Explain why this is a risk" value={form.riskItems[item] || ''} onChange={(e) => handleRiskChange(item, e.target.value)} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', minHeight: '40px' }} />
          </div>
        ))}

        <h2 style={{ color: '#555' }}>Financial Summary</h2>
        <input type="file" accept=".xlsx,.xlsm" onChange={handleFileChange} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />

        {form.financialSummary && form.financialSummary.length > 0 && (
          <div style={{
            overflowX: 'auto',
            marginTop: '15px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <table style={{
              borderCollapse: 'collapse',
              width: '100%',
              backgroundColor: '#fff',
              fontSize: '13px'
            }}>
              <tbody>
                {form.financialSummary.map((row, rowIndex) => {
                  // Embolden rows 1, 3, and 9 (array indices 0, 2, 8)
                  const boldRowIndices = [0, 2, 8];
                  const isBoldRow = boldRowIndices.includes(rowIndex);

                  return (
                    <tr key={rowIndex} style={{
                      backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#fafafa'
                    }}>
                      {row.map((cell, cellIndex) => {
                        // Calculate min width - widen column 4 (index 3)
                        let minWidth = '100px';
                        if (cellIndex === 0) minWidth = '180px';
                        if (cellIndex === 3) minWidth = '130px';

                        return (
                          <td
                            key={cellIndex}
                            style={{
                              border: '1px solid #ddd',
                              padding: '14px 16px',
                              fontWeight: isBoldRow ? 'bold' : cell.style?.bold ? 'bold' : 'normal',
                              fontStyle: cell.style?.italic ? 'italic' : 'normal',
                              textAlign: cell.style?.alignment?.horizontal || 'left',
                              whiteSpace: 'nowrap',
                              minWidth: minWidth,
                              verticalAlign: 'middle',
                              height: '50px',
                              fontSize: '13px'
                            }}
                          >
                            {cell.value === '' || cell.value === null || cell.value === undefined ? '\u00A0' : String(cell.value)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <button type="button" onClick={handleSave} style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', alignSelf: 'center' }}>{isEditMode ? 'Update Project' : 'Save Project'}</button>
      </div>
    </div>
  );
}